/**
 * Query hook for the Fixtures tab. Paginates the league schedule and maps to
 * LeagueFixture[] so we can reuse the ScoreboardMatchRow layout and segment the
 * time-ordered list with group/round banners.
 */

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchLeagueSchedule } from '../helpers/leagueDetailApi';
import { mapScheduleMatches } from '../helpers/mapLeagueSchedule.helper';
import type { LeagueFixture } from '../helpers/leagueFixtureSections.helper';

const STALE_TIME_MS = 2 * 60 * 1000;
const DEFAULT_LIMIT = 30;

export interface UseLeagueScheduleResult {
  matches: LeagueFixture[];
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function useLeagueSchedule(
  leagueId: string | undefined,
  enabled = true,
  limit: number = DEFAULT_LIMIT
): UseLeagueScheduleResult {
  const canFetch = enabled && typeof leagueId === 'string' && leagueId.length > 0;
  const query = useInfiniteQuery({
    queryKey: ['leagueDetail', 'schedule', leagueId, limit],
    queryFn: ({ pageParam }) => fetchLeagueSchedule(leagueId ?? '', pageParam, limit),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length : undefined),
    enabled: canFetch,
    staleTime: STALE_TIME_MS,
  });

  const matches = useMemo(
    () => mapScheduleMatches(query.data?.pages.flatMap((p) => p.matches) ?? []),
    [query.data]
  );

  return {
    matches,
    isLoading: query.isLoading,
    isError: query.isError,
    isEmpty: !query.isLoading && matches.length === 0,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}
